import { useCallback, useRef, useState } from 'react';

/**
 * Custom Places Autocomplete Hook - Dual API Support with Cost Optimization
 *
 * REPLACES: use-places-autocomplete library
 * MAINTAINS: Same interface for seamless migration
 * SUPPORTS: Both legacy data compatibility and new API efficiency
 * OPTIMIZED: Session tokens for cost reduction (up to 60% savings)
 *
 * This hook provides the same interface as the original use-places-autocomplete
 * but uses the New Places API under the hood while maintaining compatibility
 * with existing components and data structures.
 */

/**
 * Development-only logging utilities
 */
function devLog(message: string, data?: unknown) {
  if (process.env.NODE_ENV === 'development') {
    // biome-ignore lint/suspicious/noConsole: should remove these
    console.log(message, data);
  }
}

function devWarn(message: string, data?: unknown) {
  if (process.env.NODE_ENV === 'development') {
    // biome-ignore lint/suspicious/noConsole: should remove these
    console.warn(message, data);
  }
}

function devError(message: string, data?: unknown) {
  // Always log errors, but with less noise in production
  if (process.env.NODE_ENV === 'development') {
    // biome-ignore lint/suspicious/noConsole: should remove these
    console.error(message, data);
  } else {
    // biome-ignore lint/suspicious/noConsole: should remove these
    console.error(message);
  }
}

/**
 * Generate a unique session token for cost optimization
 * Session tokens group related autocomplete and place details requests
 * Can reduce API costs by up to 60% for the New Places API
 */
function generateSessionToken(): string {
  return `session_${Math.random().toString(36).substr(2, 16)}${Date.now().toString(36)}`;
}

interface AutocompleteCacheEntry {
  predictions: PlacePrediction[];
  timestamp: number;
}

const AUTOCOMPLETE_CACHE_TTL_MS = 5 * 60 * 1_000; // 5 minutes — long enough to reuse within a session
const AUTOCOMPLETE_CACHE_MAX_SIZE = 50;
const autocompleteCache = new Map<string, AutocompleteCacheEntry>();

function getCachedAutocompletePredictions(cacheKey: string): PlacePrediction[] | null {
  const entry = autocompleteCache.get(cacheKey);
  if (!entry) {
    return null;
  }

  const isExpired = Date.now() - entry.timestamp > AUTOCOMPLETE_CACHE_TTL_MS;
  if (isExpired) {
    autocompleteCache.delete(cacheKey);
    return null;
  }

  return entry.predictions;
}

function setCachedAutocompletePredictions(cacheKey: string, predictions: PlacePrediction[]): void {
  if (autocompleteCache.size >= AUTOCOMPLETE_CACHE_MAX_SIZE) {
    const oldestKey = autocompleteCache.keys().next().value;
    if (oldestKey) {
      autocompleteCache.delete(oldestKey);
    }
  }
  autocompleteCache.set(cacheKey, { predictions, timestamp: Date.now() });
}

export interface PlacePrediction {
  place_id: string; // Keep legacy naming for compatibility
  description: string;
  // Additional fields for full compatibility
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
  types?: string[];
}

export interface PlacesAutocompleteState {
  ready: boolean;
  value: string;
  suggestions: {
    status: 'OK' | 'ZERO_RESULTS' | 'ERROR';
    data: PlacePrediction[];
  };
  setValue: (value: string, shouldFetchData?: boolean) => void;
  clearSuggestions: () => void;
  refreshSessionToken: () => void;
  sessionToken: string;
  usedNewApiForAutocomplete: boolean;
}

interface UsePlacesAutocompleteOptions {
  requestOptions?: {
    location?: google.maps.LatLng;
    radius?: number;
    types?: string[];
  };
  debounce?: number;
}

/**
 * Get and validate the Google API key
 * @param useNewApi - Whether to get the new Places API key or legacy key
 */
function getGoogleApiKey(useNewApi = false): string {
  let apiKey: string | undefined;

  if (useNewApi) {
    // Try new Places API key first (for production and local development)
    apiKey = process.env.REACT_APP_PLACES_API_KEY;

    if (!apiKey) {
      devError('❌ New Places API key not found. Checked: REACT_APP_PLACES_API_KEY');
      throw new Error('New Places API key is required');
    }
  } else {
    // Use legacy/original API key
    apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

    if (!apiKey) {
      devError('❌ REACT_APP_GOOGLE_API_KEY is not defined in environment variables');
      throw new Error('Google API key is required');
    }
  }

  if (apiKey.startsWith('%') && apiKey.endsWith('%')) {
    devError('❌ API key appears to be a build-time placeholder that was not replaced');
    throw new Error('Google API key was not properly configured during build');
  }

  return apiKey;
}

/**
 * Create standard headers for New Places API requests
 */
function createPlacesApiHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'X-Goog-Api-Key': getGoogleApiKey(true), // Use new Places API key
    'X-Goog-FieldMask':
      'suggestions.placePrediction.place,suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat,suggestions.placePrediction.types',
  };
}

/**
 * Custom usePlacesAutocomplete hook using New Places API
 */
export default function usePlacesAutocomplete(
  options: UsePlacesAutocompleteOptions = {}
): PlacesAutocompleteState {
  const [ready] = useState(true); // New API is always ready
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState<{
    status: 'OK' | 'ZERO_RESULTS' | 'ERROR';
    data: PlacePrediction[];
  }>({
    status: 'ZERO_RESULTS',
    data: [],
  });

  const debounceTimer = useRef<NodeJS.Timeout>();
  const abortController = useRef<AbortController>();
  const sessionToken = useRef<string>(generateSessionToken()); // Cost optimization
  const [usedNewApiForAutocomplete, setUsedNewApiForAutocomplete] = useState(false); // Track API used

  const fetchSuggestions = useCallback(
    async (input: string) => {
      const trimmedInput = input.trim();
      if (!trimmedInput) {
        setSuggestions({ status: 'ZERO_RESULTS', data: [] });
        return;
      }

      const cacheKey = trimmedInput.toLowerCase();
      const cachedPredictions = getCachedAutocompletePredictions(cacheKey);
      if (cachedPredictions) {
        devLog('🗄️ Autocomplete cache hit for:', cacheKey);
        setSuggestions({
          status: cachedPredictions.length > 0 ? 'OK' : 'ZERO_RESULTS',
          data: cachedPredictions,
        });
        return;
      }

      if (abortController.current) {
        abortController.current.abort();
      }
      abortController.current = new AbortController();

      try {
        const requestBody: Record<string, unknown> = {
          input: trimmedInput,
        };

        // Add session token for cost optimization
        if (sessionToken.current) {
          requestBody.sessionToken = sessionToken.current;
        }

        // Add location bias in New Places API format
        if (options?.requestOptions?.location && options?.requestOptions?.radius) {
          requestBody.locationBias = {
            circle: {
              center: {
                latitude: options?.requestOptions?.location.lat(),
                longitude: options?.requestOptions?.location.lng(),
              },
              radius: options?.requestOptions?.radius,
            },
          };
        }

        // Add type restrictions in New Places API format
        if (options?.requestOptions?.types && options?.requestOptions?.types.length > 0) {
          requestBody.includedPrimaryTypes = options?.requestOptions?.types;
        }

        try {
          // TRY NEW PLACES API FIRST
          devLog('🔍 Attempting New Places API autocomplete request:', {
            input: input.trim(),
            requestBody,
          });

          const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
            method: 'POST',
            headers: createPlacesApiHeaders(),
            body: JSON.stringify(requestBody),
            signal: abortController.current.signal,
          });

          devLog('📡 New API Response status:', {
            status: response.status,
            statusText: response.statusText,
          });

          if (!response.ok) {
            const errorText = await response.text();
            devWarn('⚠️ New Places API failed, falling back to Legacy API:', {
              status: response.status,
              statusText: response.statusText,
              errorText,
            });
            throw new Error(`New Places API HTTP error! status: ${response.status} - ${errorText}`);
          }

          const data = await response.json();
          devLog('📦 New API Response data:', data);

          if (data.suggestions && data.suggestions.length > 0) {
            setUsedNewApiForAutocomplete(true);

            // Convert new API response to legacy format for compatibility
            const predictions: PlacePrediction[] = data.suggestions
              .filter((suggestion: { placePrediction?: unknown }) => suggestion.placePrediction)
              .map(
                (suggestion: {
                  placePrediction: {
                    placeId: string;
                    text: { text: string };
                    structuredFormat?: {
                      mainText: { text: string };
                      secondaryText: { text: string };
                    };
                    types?: string[];
                  };
                }) => ({
                  place_id: suggestion.placePrediction.placeId, // Keep legacy naming
                  description: suggestion.placePrediction.text.text,
                  structured_formatting: suggestion.placePrediction.structuredFormat
                    ? {
                        main_text:
                          suggestion.placePrediction.structuredFormat.mainText?.text ||
                          suggestion.placePrediction.text.text,
                        secondary_text:
                          suggestion.placePrediction.structuredFormat.secondaryText?.text || '',
                      }
                    : {
                        main_text: suggestion.placePrediction.text.text,
                        secondary_text: '',
                      },
                  types: suggestion.placePrediction.types || [],
                })
              );

            devLog('✅ New API successful, converted to legacy format:', predictions);
            setCachedAutocompletePredictions(cacheKey, predictions);

            setSuggestions({
              status: 'OK',
              data: predictions,
            });
          } else {
            devLog('🚫 New API returned no suggestions');
            setCachedAutocompletePredictions(cacheKey, []);
            setSuggestions({
              status: 'ZERO_RESULTS',
              data: [],
            });
          }
        } catch (newApiError) {
          devWarn('⚠️ New Places API unavailable, falling back to Legacy API:', newApiError);

          // FALLBACK TO LEGACY API
          if (window.google?.maps?.places?.AutocompleteService) {
            devLog('🔄 Using Legacy Places API as fallback');

            const service = new window.google.maps.places.AutocompleteService();
            const request: google.maps.places.AutocompletionRequest = { input: trimmedInput };

            // Add location bias if provided
            if (options.requestOptions?.location && options.requestOptions?.radius) {
              request.location = options.requestOptions.location;
              request.radius = options.requestOptions.radius;
            }

            // Add type restrictions if provided
            if (options.requestOptions?.types && options.requestOptions.types.length > 0) {
              request.types = options.requestOptions.types;
            }

            service.getPlacePredictions(request, (predictions, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                setUsedNewApiForAutocomplete(false);

                // Convert legacy API response to our format
                const convertedPredictions: PlacePrediction[] = predictions.map((prediction) => ({
                  place_id: prediction.place_id,
                  description: prediction.description,
                  structured_formatting: prediction.structured_formatting,
                  types: prediction.types,
                }));

                devLog('✅ Legacy API successful:', convertedPredictions);
                setCachedAutocompletePredictions(cacheKey, convertedPredictions);

                setSuggestions({
                  status: 'OK',
                  data: convertedPredictions,
                });
              } else {
                devError('❌ Legacy API also failed:', status);
                setSuggestions({
                  status: 'ERROR',
                  data: [],
                });
              }
            });
          } else {
            devError('❌ Google Maps JavaScript API not loaded');
            setSuggestions({
              status: 'ERROR',
              data: [],
            });
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // Request was cancelled, ignore
          return;
        }

        devError('Places Autocomplete error:', error);
        setSuggestions({
          status: 'ERROR',
          data: [],
        });
      }
    },
    [options.requestOptions]
  );

  const handleSetValue = useCallback(
    (newValue: string, shouldFetchData = true) => {
      setValue(newValue);

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      if (shouldFetchData && newValue.trim()) {
        const debounceMs = options.debounce || 300;
        debounceTimer.current = setTimeout(() => {
          fetchSuggestions(newValue);
        }, debounceMs);
      } else if (!shouldFetchData || !newValue.trim()) {
        setSuggestions({ status: 'ZERO_RESULTS', data: [] });
      }
    },
    [fetchSuggestions, options.debounce]
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions({ status: 'ZERO_RESULTS', data: [] });

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (abortController.current) {
      abortController.current.abort();
    }
  }, []);

  const refreshSessionToken = useCallback(() => {
    sessionToken.current = generateSessionToken();
    devLog('🔄 Session token refreshed for next autocomplete search');
  }, []);

  return {
    ready,
    value,
    suggestions,
    setValue: handleSetValue,
    clearSuggestions,
    refreshSessionToken,
    sessionToken: sessionToken.current,
    usedNewApiForAutocomplete,
  };
}

// Re-export essential functions for compatibility with existing components
export { getDetails, getGeocode, getLatLng } from '../utils/places-api.utils';

// Re-export types for compatibility
export type { UsePlacesAutocompleteOptions };
// Export session token for cost optimization across related requests
export { generateSessionToken };
